import { buildIncidentPayload } from './incidentEditor';
import { format_date } from './utils';

const normalizeText = (value) => {
    if (value === null || value === undefined) return null;

    const normalized = String(value).trim();
    return normalized.length > 0 ? normalized : null;
};

export const getIncidentVeloviewerUrl = (incident) =>
    normalizeText(incident && incident.incident_veloviewer_url);

export const hasVeloviewerConflict = (sourceIncident, targetIncident) => {
    const sourceUrl = getIncidentVeloviewerUrl(sourceIncident);
    const targetUrl = getIncidentVeloviewerUrl(targetIncident);

    return Boolean(sourceUrl && targetUrl && sourceUrl !== targetUrl);
};

export const getDefaultVeloviewerUrl = (sourceIncident, targetIncident) =>
    getIncidentVeloviewerUrl(targetIncident) || getIncidentVeloviewerUrl(sourceIncident);

const mergeServerManagedFields = [
    'incident_environment_type',
    'incident_location_latitude',
    'incident_location_longitude',
    'incident_type',
    'incident_weather_end',
    'incident_weather_rain',
    'incident_weather_start',
    'incident_weather_wind',
];

const formatIncidentTimestamp = (timestamp) => {
    if (!timestamp) return timestamp;

    if (timestamp instanceof Date) {
        return format_date(timestamp);
    }

    const parsedTimestamp = new Date(timestamp);
    if (Number.isNaN(parsedTimestamp.getTime())) {
        return timestamp;
    }

    return format_date(parsedTimestamp);
};

export const buildIncidentMergeRequest = ({
    sourceIncident,
    targetIncident,
    selectedVeloviewerUrl,
    targetUpdates = {},
    incidentCauses = [],
    incidentInvolvedRiders = [],
}) => {
    if (!sourceIncident || !targetIncident) {
        throw new Error('Both a source and destination incident are required.');
    }

    const sourceIncidentId = Number(sourceIncident.incident_id);
    const targetIncidentId = Number(targetIncident.incident_id);

    if (!Number.isInteger(sourceIncidentId) || !Number.isInteger(targetIncidentId)) {
        throw new Error('Both incidents must have a valid ID.');
    }

    if (sourceIncidentId === targetIncidentId) {
        throw new Error('An incident cannot be merged into itself.');
    }

    const sourceUrl = getIncidentVeloviewerUrl(sourceIncident);
    const targetUrl = getIncidentVeloviewerUrl(targetIncident);
    const normalizedSelection = normalizeText(selectedVeloviewerUrl);

    if (hasVeloviewerConflict(sourceIncident, targetIncident)) {
        if (!normalizedSelection) {
            throw new Error('Choose or enter which VeloViewer URL should be kept.');
        }
    }

    const request = { ...targetIncident };
    delete request.causes;
    delete request.riders;
    delete request.stage;

    request.incident_id = targetIncidentId;
    request.incident_race_stage_id = targetIncident.incident_race_stage || targetIncident.stage?.race_stage_id;
    request.incident_timestamp = formatIncidentTimestamp(targetIncident.incident_timestamp);
    delete request.incident_race_stage;

    Object.assign(request, targetUpdates);
    request.incident_veloviewer_url = normalizedSelection || targetUrl || sourceUrl || '';
    request.incident_causes = incidentCauses;
    request.incident_involved_riders = incidentInvolvedRiders;

    return buildIncidentPayload(request, { omitKeys: mergeServerManagedFields });
};

export const getIncidentMergeLabel = (incident) => {
    const raceName = incident.race_name
        || incident.racename
        || incident.stage?.race_edition?.race?.name
        || 'Unknown race';
    const timestamp = incident.incident_timestamp || incident.incident_ts;
    const date = timestamp ? new Date(timestamp) : null;
    const dateLabel = date && !Number.isNaN(date.getTime())
        ? date.toLocaleDateString()
        : 'Unknown date';

    return `#${incident.incident_id} — ${raceName} — ${dateLabel}`;
};
